import Head from "next/head";
import Link from "next/link";
import { useState } from "react";
import { toast } from "react-hot-toast";
import { Header } from "@/components/LandingPage/Header";
import { Footer } from "@/components/LandingPage/Footer";
import { Container } from "@/components/LandingPage/Container";
import logger from "@/utils/logger";

export default function PrettifyPage() {
  const [jsonInput, setJsonInput] = useState("");
  const [jsonOutput, setJsonOutput] = useState("");
  const [error, setError] = useState("");

  const prettifyJson = () => {
    try {
      if (!jsonInput.trim()) {
        setError("Please enter some JSON to prettify");
        setJsonOutput("");
        return;
      }

      const parsed = JSON.parse(jsonInput);
      const formatted = JSON.stringify(parsed, null, 2);
      setJsonOutput(formatted);
      setError("");
      
      // Log the prettify action to logsnag
      logger.log(
        "prettify",
        "JSON Prettified",
        `User successfully prettified JSON (${jsonInput.length} characters input)`
      );

    } catch (err) {
      setError("Invalid JSON format. Please check your syntax.");
      setJsonOutput("");
    }
  };

  const clearAll = () => {
    setJsonInput("");
    setJsonOutput("");
    setError("");
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(jsonOutput);
      toast.success("JSON copied to clipboard!");
    } catch (err) {
      console.error("Failed to copy to clipboard:", err);
      toast.error("Failed to copy to clipboard");
    }
  };

  return (
    <div className="bg-[#1E1E1E] min-h-screen">
      <Header dark={false} />

      <main className="pt-32 pb-20">
        <Container className="max-w-7xl">
          <div className="flex flex-col items-center justify-center gap-2">
            <h1 className="text-4xl font-bold tracking-tight text-white sm:text-6xl">
              JSON Prettifier
            </h1>

            <p className="text-sm text-gray-400 ">
              No data is stored on our servers.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8 mt-8">
            {/* Input Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-white">Input JSON</h2>
              </div>
              <textarea
                value={jsonInput}
                onChange={(e) => setJsonInput(e.target.value)}
                placeholder="Paste your JSON here..."
                className="w-full h-80 p-4 bg-gray-800 border border-gray-600 rounded-lg text-gray-100 font-mono text-sm resize-none focus:ring-2 focus:ring-purple focus:border-transparent"
                aria-label="JSON input"
              />
            </div>

            {/* Output Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-white">
                  Formatted JSON
                </h2>
                <button
                  onClick={copyToClipboard}
                  disabled={!jsonOutput.trim()}
                  className={`text-sm transition-colors ${
                    jsonOutput.trim()
                      ? "text-purple hover:text-purple-300 cursor-pointer"
                      : "text-gray-500 cursor-not-allowed"
                  }`}
                >
                  Copy to Clipboard
                </button>
              </div>
              <textarea
                value={jsonOutput}
                readOnly
                placeholder="Formatted JSON will appear here..."
                className="w-full h-80 p-4 bg-gray-900 border border-gray-600 rounded-lg text-gray-100 font-mono text-sm resize-none"
                aria-label="JSON output"
              />
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-8 p-4 bg-red-900/20 border border-red-500/50 rounded-lg">
              <p className="text-red-300 font-medium">Error: {error}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-4 justify-center mb-8">
            <button
              onClick={prettifyJson}
              disabled={!jsonInput.trim()}
              className={`px-6 py-3 font-semibold rounded-lg transition-colors focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 ${
                jsonInput.trim()
                  ? "bg-purple hover:bg-purple-600 text-white focus:ring-purple"
                  : "bg-gray-600 text-gray-400 cursor-not-allowed"
              }`}
            >
              Prettify JSON
            </button>
            <button
              onClick={clearAll}
              className="px-6 py-3 border border-gray-600 hover:border-gray-500 text-gray-300 hover:text-white font-semibold rounded-lg transition-colors focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 focus:ring-offset-gray-900"
            >
              Clear All
            </button>
          </div>

          {/* Call to Action - Store JSON */}
          {jsonOutput && (
            <div className="bg-gradient-to-r from-purple/10 to-purple/5 border border-purple/20 rounded-xl p-8 mb-12 text-center">
              <div className="mb-4">
                <svg
                  className="w-12 h-12 text-purple mx-auto mb-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"
                  />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">
                Ready to Store & Manage Your JSON?
              </h3>
              <p className="text-gray-300 text-lg mb-6 max-w-2xl mx-auto">
                Get started with myJson.online&apos;s powerful REST API. Store
                your JSON with versioning, private collections, and analytics.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Link
                  href="/signup?utm_source=prettify"
                  className="px-8 py-4 bg-purple hover:bg-purple-600 text-white font-bold rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-purple/25 inline-flex items-center gap-2"
                >
                  Get started for free
                </Link>
              </div>
              <div className="mt-4 flex items-center justify-center gap-6 text-sm text-gray-400">
                <div className="flex items-center gap-1">
                  <svg
                    className="w-4 h-4 text-green-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  REST API
                </div>
                <div className="flex items-center gap-1">
                  <svg
                    className="w-4 h-4 text-green-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  JSON Versioning
                </div>
                <div className="flex items-center gap-1">
                  <svg
                    className="w-4 h-4 text-green-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  Private Collections
                </div>
                <div className="flex items-center gap-1">
                  <svg
                    className="w-4 h-4 text-green-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  Analytics
                </div>
              </div>
            </div>
          )}
        </Container>
      </main>

      <Footer />
    </div>
  );
}
